import React from 'react';
import { Box, Container, Typography, Button, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link as ScrollLink } from 'react-scroll';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const Hero: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box
      component="section"
      id="hero"
      aria-labelledby="hero-heading"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'rgb(13, 13, 37)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: '80px', sm: '70px', md: '80px' }, // Add top padding to avoid navbar overlap
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 70%, rgba(0, 245, 255, 0.1) 0%, rgba(255, 0, 255, 0) 50%)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '0.95fr 1.15fr' },
              gap: { xs: 4, sm: 6, lg: 7 },
              alignItems: 'center',
              px: { xs: 2, sm: 3, md: 0 },
            }}
          >
            <Box>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h1"
                  id="hero-heading"
                  sx={{
                    fontSize: {
                      xs: '2rem',
                      sm: '2.8rem',
                      md: '3.5rem',
                      lg: '4rem',
                    },
                    fontWeight: 800,
                    mb: 2,
                    background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: { xs: 1.1, sm: 1.2 },
                    letterSpacing: '-0.02em',
                    wordBreak: 'keep-all',
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  {t('hero.title')}
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 3,
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 400,
                    lineHeight: 1.4,
                    fontSize: { 
                      xs: '1.1rem',    // Smaller subtitle on mobile
                      sm: '1.3rem',    // Small screens
                      md: '1.5rem',    // Medium and up
                    },
                    textAlign: { xs: 'center', md: 'left' }, // Center on mobile
                  }}
                >
                  {t('hero.subtitle')}
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                    maxWidth: '600px',
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' }, // Responsive font size
                    textAlign: { xs: 'center', md: 'left' }, // Center on mobile
                  }}
                >
                  {t('hero.description')}
                </Typography>
              </motion.div>

              {/* Four pillars chips */}
              <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 2,
                    mb: 1,
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  {[
                    t('hero.pillarConnect'),
                    t('hero.pillarUnderstand'),
                    t('hero.pillarBuild'),
                    t('hero.pillarDeploy'),
                  ].map((pillar) => (
                    <Chip
                      key={pillar}
                      label={pillar}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.06)',
                        color: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        backdropFilter: 'blur(8px)',
                      }}
                    />
                  ))}
                </Box>
              </motion.div>

              {/* CTA Buttons */}
              <Box
                sx={{
                  mt: { xs: 4, md: 6 },
                  display: 'flex',
                  gap: { xs: 2, sm: 3 },
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  flexWrap: 'wrap',
                }}
              >
                <ScrollLink to="demo" smooth={true} duration={500} offset={-70}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PlayCircleOutlineIcon />}
                    sx={{
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontSize: '1.05rem',
                      background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                      color: 'white',
                      boxShadow: '0 12px 32px rgba(0, 245, 255, 0.35)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 16px 40px rgba(255, 0, 255, 0.45)',
                      },
                    }}
                  >
                    {t('hero.primaryCta')}
                  </Button>
                </ScrollLink>
                <ScrollLink to="platform" smooth={true} duration={500} offset={-70}>
                  <Button
                    variant="outlined"
                    size="large"
                    endIcon={<ArrowDownwardIcon />}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.45)',
                      color: 'white',
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontSize: '1.05rem',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.06)',
                      },
                    }}
                  >
                    {t('hero.secondaryCta')}
                  </Button>
                </ScrollLink>
              </Box>
            </Box>

            {/* Dashboard Preview — image fills the column at its natural ratio */}
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  width: '100%',
                  borderRadius: { xs: '16px', md: '20px' },
                  overflow: 'hidden',
                  border: '1px solid rgba(255, 255, 255, 0.14)',
                  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.4)',
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  mt: { xs: 2, lg: 0 },
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    borderRadius: 'inherit',
                    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.06)',
                    zIndex: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/images/generated-app-health-dashboard.png"
                  alt={t('hero.imageAlt')}
                  loading="eager"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              </Box>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Hero; 